/**
 * The vocabulary of set pieces — what may go in `map_sku_split.piece`.
 *
 * Until 2026-10-03 a piece was a letter: boxes A and B, occasionally more. LSK then asked for an
 * UPPER PLATFORM, a LOWER PLATFORM and a SIDE (`UPF`, `LPF`, `SD`), and the list was closed to
 * those. On 2026-10-04 it was opened — *"open up any split that they want to do"* — after `AA/BB/CC`
 * was refused. Staff type the pieces in a column beside the item code (`UPF/LPF/SD`, `AA/BB/CC`).
 *
 * What remains is what a piece must be to work at all: letters and digits only, because it becomes
 * part of a product code; at most 8, the width of the column; and at least two per set, none
 * repeated. A typo is no longer refused — `UFP` is a valid name. The preview shows every code and
 * name before anything is created, and re-splitting retires a mistaken mapping (retirePieces.ts).
 */

const PIECE = /^[A-Z0-9]{1,8}$/;

/** Letters and digits, 1–8 characters, upper case. */
export function isPiece(piece: string): boolean {
  return PIECE.test(piece);
}

/**
 * How a piece is appended to its parent's code.
 *
 * A single letter goes on directly — `…FB1A` — because the 63,383 existing pairs are named that way
 * and every tag already printed carries those codes. Anything else takes a hyphen — `…FB12P-UPF`,
 * `…FB12P-1` — which LSK chose on 2026-10-03; it also keeps `…2P` + `SD` from reading as a code
 * ending `PSD`, and a digit from reading as part of the size.
 */
export function pieceSuffix(piece: string): string {
  return /^[A-Z]$/.test(piece) ? piece : `-${piece}`;
}

export type ParsedPieces = { ok: true; pieces: string[] } | { ok: false; error: string };

/** `AB`, `ABC` … `ABCDEF`: the original one-letter-per-box form, counting up from A. */
const isLetterRun = (run: string): boolean =>
  run.length >= 2 && run === 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.slice(0, run.length);

/**
 * Read the pieces column: `UPF/LPF/SD`, `aa, bb, cc`, `A+B` — or `AB` / `ABCDEF`, the original
 * form, where an unseparated run counting up from A is one piece per letter.
 *
 * Any OTHER unseparated run is refused with a request to separate it. `UPF` read letter by letter
 * would split a bed into U, P and F; `AABBCC` is obviously three pieces with the slashes forgotten.
 *
 * Only `/ , + ;` and spaces separate. A hyphen or a bracket is part of a token, which is what stops
 * an item code such as `BFJOY152(12)FB1AB` parsing as a list of pieces (parseSeparateLines).
 */
export function parsePieces(spec: string): ParsedPieces {
  const tokens = spec
    .toUpperCase()
    .split(/[/,+;\s]+/)
    .filter(Boolean);
  if (tokens.length === 0) return { ok: false, error: 'no pieces given' };

  let pieces = tokens;
  if (tokens.length === 1) {
    const run = tokens[0]!;
    if (!isLetterRun(run)) {
      return {
        ok: false,
        error: isPiece(run)
          ? `"${run}" — separate the pieces with /, e.g. UPF/LPF/SD (AB and ABCDEF are the only ` +
            `run-together forms)`
          : `"${run}" is not a list of pieces — letters and digits only, separated by /`,
      };
    }
    pieces = run.split('');
  }

  const bad = pieces.filter((p) => !isPiece(p));
  if (bad.length > 0) {
    return {
      ok: false,
      error:
        `${bad.map((b) => `"${b}"`).join(', ')} — a piece is letters and digits only, at most 8 ` +
        `characters`,
    };
  }
  if (pieces.length < 2) return { ok: false, error: 'a set needs at least two pieces' };
  if (new Set(pieces).size !== pieces.length) {
    return { ok: false, error: 'a piece is listed twice' };
  }
  return { ok: true, pieces };
}
